import React from 'react';
import { Handshake } from '@phosphor-icons/react';

interface SnapshotRow {
    engagementId: string | number;
    progress: number;
    completedSessions: number;
    totalSessions: number;
    programName?: string;
    nextSessionDate?: string;
    status: string;
    coachName?: string;
    totalGoals: number;
    completedGoals: number;
}

interface MyCoachingSnapshotWidgetProps {
    data?: SnapshotRow[] | null;
    loading?: boolean;
    error?: string;
}

const formatShortDate = (dateStr?: string) => {
    if (!dateStr) return '\u2014';
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

export const MyCoachingSnapshotWidget: React.FC<MyCoachingSnapshotWidgetProps> = ({ data, loading, error }) => {
    const rows = Array.isArray(data) ? data : [];
    const eng = rows.length > 0 ? rows[0] : null;

    return (
        <div className="flex flex-col h-full">
            <div className="flex-1 overflow-auto px-4 pb-4">
                {loading ? (
                    <div className="flex items-center justify-center h-full">
                        <div className="w-6 h-6 border-3 border-gray-200 border-t-primary rounded-full animate-spin" />
                    </div>
                ) : error ? (
                    <div className="flex items-center justify-center h-full text-red-500 text-sm">{error}</div>
                ) : !eng ? (
                    <div className="flex flex-col items-center justify-center h-full text-gray-400 gap-2">
                        <Handshake className="w-8 h-8" />
                        <span className="text-sm">No active coaching</span>
                    </div>
                ) : (
                    <>
                        {/* Coach & program header */}
                        <div className="flex items-center gap-2 mb-3">
                            <div className="w-8 h-8 rounded-full bg-cyan-100 flex items-center justify-center text-cyan-700 text-xs font-bold">
                                {eng.coachName ? eng.coachName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) : '??'}
                            </div>
                            <div>
                                <div className="text-sm font-medium text-gray-900">{eng.coachName || 'Coach'}</div>
                                <div className="text-[10px] text-gray-400">{eng.programName || 'Coaching'}</div>
                            </div>
                        </div>

                        {/* Progress ring */}
                        <div className="flex items-center justify-center mb-3">
                            <div className="relative w-20 h-20">
                                <svg className="w-full h-full -rotate-90" viewBox="0 0 36 36">
                                    <path d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                                        fill="none" stroke="#e5e7eb" strokeWidth="3" />
                                    <path d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                                        fill="none" stroke="#0e7490" strokeWidth="3"
                                        strokeDasharray={`${Number(eng.progress) || 0}, 100`} strokeLinecap="round" />
                                </svg>
                                <div className="absolute inset-0 flex items-center justify-center">
                                    <span className="text-lg font-bold text-cyan-700">{Number(eng.progress) || 0}%</span>
                                </div>
                            </div>
                        </div>

                        {/* Stats grid */}
                        <div className="grid grid-cols-3 gap-2">
                            <div className="text-center p-2 bg-cyan-50 rounded-lg">
                                <div className="text-lg font-bold text-cyan-700">
                                    {Number(eng.completedSessions) || 0}/{Number(eng.totalSessions) || 0}
                                </div>
                                <div className="text-[10px] text-gray-400">Sessions</div>
                            </div>
                            <div className="text-center p-2 bg-emerald-50 rounded-lg">
                                <div className="text-lg font-bold text-emerald-600">
                                    {Number(eng.completedGoals) || 0}/{Number(eng.totalGoals) || 0}
                                </div>
                                <div className="text-[10px] text-gray-400">Goals Done</div>
                            </div>
                            <div className="text-center p-2 bg-amber-50 rounded-lg">
                                <div className="text-lg font-bold text-amber-600">{formatShortDate(eng.nextSessionDate)}</div>
                                <div className="text-[10px] text-gray-400">Next Session</div>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};
